import type { ReactNode } from 'react';
import { useCurrentUser } from '../auth/AuthProvider.js';
import { useSessionValue } from '../lib/preferences.js';
import { type ColumnSort, nextSort, type SortValue, sortRows } from '@k-management/shared';
import { Icon } from './Icon.js';

export { amountSort } from '@k-management/shared';

export interface TableSorting<Row> {
  /** The rows in the chosen order (the same array when not sorted). */
  rows: Row[];
  sort: ColumnSort | null;
  toggle: (key: string) => void;
  sortable: (key: string) => boolean;
}

/** Where useTableSort keeps the sort of the table `id` for `email`. */
export function tableSortKey(id: string, email: string): string {
  return `sort.${id}.${email}`;
}

/**
 * The sort of a plain table, kept while the browser stays open like the
 * filters of the lists. `values` gives what each sortable column sorts by.
 */
export function useTableSort<Row>(
  id: string,
  rows: Row[],
  values: Record<string, (row: Row) => SortValue>,
): TableSorting<Row> {
  const user = useCurrentUser();
  const [sort, setSort] = useSessionValue<ColumnSort | null>(tableSortKey(id, user.email), null);
  const value = sort ? values[sort.key] : undefined;
  return {
    rows: sortRows(rows, sort, value),
    sort: value ? sort : null,
    toggle: (key) => setSort(nextSort(sort, key)),
    sortable: (key) => key in values,
  };
}

/** The label of a heading with its arrow: ↕ until sorted by it, then ▲ or ▼. */
export function SortButton({
  sorted,
  onClick,
  children,
}: {
  sorted: 1 | -1 | null;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className="sort-button" onClick={onClick}>
      {children}
      {/* Every sortable column shows it can be sorted (11/10/2026). */}
      <span className={sorted ? 'sort-mark sorted' : 'sort-mark'} aria-hidden>
        <Icon name={sorted === 1 ? 'up' : sorted === -1 ? 'down' : 'sort'} size={13} />
      </span>
    </button>
  );
}

export function ariaSort(sorted: 1 | -1 | null): 'ascending' | 'descending' | undefined {
  return sorted === 1 ? 'ascending' : sorted === -1 ? 'descending' : undefined;
}

/** A heading of a plain table that sorts it by `column`. */
export function SortHeader<Row>({
  sort,
  column,
  numeric = false,
  className,
  children,
}: {
  sort: TableSorting<Row>;
  column: string;
  numeric?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const classes = [numeric ? 'numeric' : '', className ?? ''].filter(Boolean).join(' ');
  if (!sort.sortable(column)) return <th className={classes || undefined}>{children}</th>;
  const sorted = sort.sort?.key === column ? sort.sort.direction : null;
  return (
    <th className={classes || undefined} aria-sort={ariaSort(sorted)}>
      <SortButton sorted={sorted} onClick={() => sort.toggle(column)}>
        {children}
      </SortButton>
    </th>
  );
}
